import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  BarChart3,
  LogOut,
  Settings,
  Menu,
  X,
  SlidersHorizontal,
  Sparkles,
  Gauge,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useHealthCheck } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { BridgelayerWordmark, BridgelayerMark } from "@/components/brand/Logo";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { TrashCanProvider } from "@/lib/trash-context";
import { TrashCan } from "@/components/trash/TrashCan";
import { NotificationsBell } from "@/components/layout/NotificationsBell";

interface NavItem {
  href: string;
  label: string;
  icon: typeof BarChart3;
}

const NAV_ITEMS: NavItem[] = [
  { href: "/", label: "Dashboard", icon: BarChart3 },
  { href: "/discovery", label: "Discovery Engine", icon: SlidersHorizontal },
  { href: "/cockpit", label: "Quote Cockpit", icon: Gauge },
  { href: "/promptHQ", label: "Prompt Council", icon: Sparkles },
  { href: "/settings", label: "Settings", icon: Settings },
];

export function Shell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { reduce, t, variants } = useMotion();
  const { data: health } = useHealthCheck();
  const { user, signOut } = useAuth();

  const sidebarVariants = variants.drawer;

  const sidebarContent = (
    <div className="h-full flex flex-col">
      <div className="h-16 flex items-center px-5 border-b glass-divider shrink-0 justify-between gap-2">
        <Link href="/" className="inline-flex items-center" data-testid="link-brand">
          <BridgelayerWordmark size="md" />
        </Link>
        <NotificationsBell />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 flex flex-col gap-1 px-3">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active =
            item.href === "/"
              ? location === "/"
              : location.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "group relative flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg",
                "transition-colors duration-200 motion-reduce:transition-none",
                active
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {active && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-0 rounded-lg bg-[hsl(var(--primary)/0.10)] border border-[hsl(var(--primary)/0.18)]"
                  transition={t.snap}
                />
              )}
              <Icon
                className={cn(
                  "w-4 h-4 relative z-10 transition-colors",
                  active ? "text-[hsl(var(--primary))]" : "",
                )}
              />
              <span className="relative z-10">{item.label}</span>
            </Link>
          );
        })}

        {/* Completed jobs trash can */}
        <div className="mt-4 pt-3 border-t border-border/40 flex flex-col items-center gap-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-1">
            Completed
          </p>
          <TrashCan />
        </div>
      </nav>

      <div className="p-4 border-t glass-divider shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Theme
          </span>
          <ThemeToggle />
        </div>

        {user?.email && (
          <div
            className="text-xs text-muted-foreground truncate px-1"
            data-testid="text-user-email"
            title={user.email}
          >
            {user.email}
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start press"
          onClick={() => signOut()}
          data-testid="button-sign-out"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Sign out
        </Button>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground pt-1">
          <span
            className={cn(
              "relative inline-flex w-2 h-2 rounded-full shrink-0",
              health?.status === "ok"
                ? "bg-[hsl(var(--success))]"
                : "bg-destructive",
            )}
          >
            {health?.status === "ok" && !reduce && (
              <span className="absolute inset-0 rounded-full bg-[hsl(var(--success))] opacity-50 animate-ping" />
            )}
          </span>
          System {health?.status === "ok" ? "Online" : "Offline"}
        </div>
      </div>
    </div>
  );

  return (
    <TrashCanProvider>
      <div className="h-dvh flex flex-col md:flex-row overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 glass border-b glass-divider flex items-center px-4 justify-between shrink-0 z-30">
          <Link href="/" className="inline-flex items-center" data-testid="link-brand-mobile">
            <BridgelayerWordmark size="sm" />
          </Link>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="button-mobile-nav"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.aside
                key="mobile-drawer"
                className="md:hidden fixed inset-y-0 left-0 z-50 w-72 glass"
                initial="closed"
                animate="open"
                exit="closed"
                variants={sidebarVariants}
              >
                {sidebarContent}
              </motion.aside>
              <motion.button
                key="mobile-overlay"
                aria-label="Close navigation overlay"
                className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
                onClick={() => setMobileOpen(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </>
          )}
        </AnimatePresence>

        {/* Desktop sidebar — fixed height, scrolls independently */}
        <aside className="hidden md:flex w-64 shrink-0 h-full glass border-r glass-divider flex-col">
          {sidebarContent}
        </aside>

        {/* Main content — takes remaining height, scrolls on its own */}
        <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 md:p-8 max-w-[1400px] mx-auto w-full">
              {children}
            </div>
          </div>
          <footer className="px-6 py-3 border-t glass-divider text-[11px] text-muted-foreground flex items-center gap-2 shrink-0">
            <BridgelayerMark size={14} className="opacity-60" />
            <span>bridgelayer · operations backend</span>
          </footer>
        </main>
      </div>
    </TrashCanProvider>
  );
}
